export const pickupSchedule = {
  timeZone: 'America/Chicago',
  windowLabel: '4–6 PM',
  endHour: 18,
  weekdays: [1, 2, 3, 4, 5],
  holidaySource: 'https://about.usps.com/newsroom/events/',
  holidayVerifiedAt: '2026-09-12',
} as const

// Store pickup policy applies USPS holiday closures to every listed carrier.
// Postal Saturday holidays stay on Saturday (not federal employee Friday leave).
// Sunday holidays also close postal service on the following Monday.
const dateKey = (date: Date) => date.toISOString().slice(0, 10)
export const calendarDate = (key: string) => new Date(`${key}T00:00:00Z`)
export function addCalendarDays(key: string, days: number) {
  const date = calendarDate(key)
  date.setUTCDate(date.getUTCDate() + days)
  return dateKey(date)
}

export function localPickupTime(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: pickupSchedule.timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', hourCycle: 'h23',
  }).formatToParts(now)
  const part = (name: string) => parts.find((entry) => entry.type === name)!.value
  return { date: `${part('year')}-${part('month')}-${part('day')}`, hour: Number(part('hour')) }
}

export function postalHolidays(year: number) {
  const holidays: Record<string, string> = {}
  const fixed = (month: number, day: number, name: string) => {
    const date = new Date(Date.UTC(year, month - 1, day))
    holidays[dateKey(date)] = name
    if (date.getUTCDay() === 0) holidays[addCalendarDays(dateKey(date), 1)] = `${name} (observed)`
  }
  const weekday = (month: number, dayOfWeek: number, occurrence: number, name: string) => {
    const first = new Date(Date.UTC(year, month - 1, 1))
    const day = 1 + (dayOfWeek - first.getUTCDay() + 7) % 7 + 7 * (occurrence - 1)
    holidays[dateKey(new Date(Date.UTC(year, month - 1, day)))] = name
  }
  fixed(1, 1, 'New Year’s Day')
  weekday(1, 1, 3, 'Martin Luther King, Jr. Day')
  weekday(2, 1, 3, 'Presidents Day')
  const lastMay = new Date(Date.UTC(year, 4, 31))
  lastMay.setUTCDate(31 - (lastMay.getUTCDay() - 1 + 7) % 7)
  holidays[dateKey(lastMay)] = 'Memorial Day'
  fixed(6, 19, 'Juneteenth')
  fixed(7, 4, 'Independence Day')
  weekday(9, 1, 1, 'Labor Day')
  weekday(10, 1, 2, 'Columbus Day')
  fixed(11, 11, 'Veterans Day')
  weekday(11, 4, 4, 'Thanksgiving Day')
  fixed(12, 25, 'Christmas Day')
  return holidays
}

export function getPickupDay(date: string) {
  const holiday = postalHolidays(Number(date.slice(0, 4)))[date]
  const weekday = calendarDate(date).getUTCDay()
  const available = !holiday && (pickupSchedule.weekdays as readonly number[]).includes(weekday)
  return { date, holiday, available, label: holiday ? 'Holiday — No Pickup' : available ? pickupSchedule.windowLabel : 'No Pickup' }
}

export function nextPickup(now = new Date()) {
  const local = localPickupTime(now)
  for (let offset = 0; offset < 15; offset++) {
    const day = getPickupDay(addCalendarDays(local.date, offset))
    if (day.available && (offset > 0 || local.hour < pickupSchedule.endHour)) return day
  }
  throw new Error('No pickup date found in the next two weeks')
}

export function formatPickupDate(date: string, options: Intl.DateTimeFormatOptions = {}) {
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', ...options }).format(calendarDate(date))
}
